import React, { useState, useCallback } from "react";
import { Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, PanelSection } from "./controls";
import { exportSelection, EXPORT_FORMATS, EXPORT_SCALES } from "../exportUtils";
import { notify } from "@/lib/notify";

// Export the current target (single selected shape, a marquee-selected group,
// or — when nothing is selected — the whole canvas) as SVG / PNG / JPG / WebP
// at 1x–4x. Lives at the bottom of the right Inspector panel.
export default function ExportPanel({ targets, background, scope }) {
  const [format, setFormat] = useState("png");
  const [scale, setScale] = useState("2");
  const [busy, setBusy] = useState(false);

  const count = targets?.length || 0;
  const isRaster = format !== "svg";

  const onExport = useCallback(async () => {
    if (!count) return notify.error("没有可导出的图形 · Nothing to export");
    setBusy(true);
    try {
      const name =
        count === 1 ? `${targets[0].type}-${targets[0].id.slice(0, 6)}` : `selection-${count}`;
      await exportSelection(targets, {
        format,
        scale: Number(scale),
        background,
        name,
      });
      notify.success(`已导出 ${format.toUpperCase()}${isRaster && Number(scale) > 1 ? ` @${scale}x` : ""}`);
    } catch (e) {
      notify.error(e.message || "导出失败 · Export failed");
    } finally {
      setBusy(false);
    }
  }, [targets, count, format, scale, background, isRaster]);

  return (
    <PanelSection title="Export · 导出">
      <p className="text-[10px] font-mono text-ink4 mb-2" data-testid="export-scope-label">
        {scope}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Format">
          <Select value={format} onValueChange={setFormat}>
            <SelectTrigger data-testid="export-format-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-chrome border-line text-foreground rounded-none">
              {EXPORT_FORMATS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Scale">
          <Select value={scale} onValueChange={setScale} disabled={!isRaster}>
            <SelectTrigger data-testid="export-scale-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm disabled:opacity-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-chrome border-line text-foreground rounded-none">
              {EXPORT_SCALES.map((s) => (
                <SelectItem key={s} value={String(s)}>{s}x</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <button
        type="button"
        onClick={onExport}
        disabled={busy || !count}
        data-testid="export-selection-btn"
        className="mt-3 w-full h-9 flex items-center justify-center gap-2 bg-[#002FA7] text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#0038C7] transition-colors disabled:opacity-40"
      >
        <Download className="h-3.5 w-3.5" /> {busy ? "导出中…" : "导出 · Export"}
      </button>
    </PanelSection>
  );
}
